




/**
 * ledian v1 でアップグレード/ダウングレードの対応は2022/4/1リリースから遅れて
 * 2022/4/18版でのリリースということになっていたが、Engine対応が間に合わない.
 * そこで、2022/4/18時点ではアップグレード/ダウングレード/再契約について制約を加えた状態で一旦リリース.
 *
 * 最終的にこちらの実装は削除され、{@link ActionToPlan}のみが使われる想定.
 *
 * @see https://github.com/my-color/front/issues/6328
 * @see https://my-color.esa.io/posts/804#Front-2
 */

import { SubscribedPaidMembershipPlan } from '@/models/api/paid-member-price/member-plan/available-membership-plan'
import {
  AllowedAction,
  AvailablePaidMembershipPlanInGroup,
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group'
import { PagePathToSubscribePaidMembership } from '@/models/paid-membership/subscribe/page-path'
import Vue, { VueConstructor } from 'vue'
import { Component, Prop } from 'vue-property-decorator'

interface Action {
  to: string
  color: string
  label: string
}
const apply: Action = {
  to: PagePathToSubscribePaidMembership.returnToTopAfterApply().toString(),
  label: '申し込む',
  color: 'gray',
}
const unapply: (plan: SubscribedPaidMembershipPlan) => Action = p => ({
  to: `/paid-membership/unsubscribe/confirm/${p.externalId}/${p.subscribeId}`,
  label: '解約する',
  color: 'primary',
})
const upgrade: (groupId: string, action: AllowedAction<'upgrade'>) => Action =
  (groupId, { planIdBeforeChange, planIdAfterChange }) => ({
    to: `/paid-membership/${groupId}/upgrade/${planIdBeforeChange}/to/${planIdAfterChange}`,
    label: '変更する',
    color: 'gray',
  })
const downgrade: (groupId: string, action: AllowedAction<'downgrade'>) => Action
  = (groupId, { planIdBeforeChange, planIdAfterChange }) => ({
    to: `/paid-membership/${groupId}/downgrade/${planIdBeforeChange}/to/${planIdAfterChange}`,
    label: '変更する',
    color: 'gray',
  })

const actionLink = (action: Action) => Vue.extend({
  name: 'ActiveLink',
  template: `
  <router-link
    v-bind:class="decorationClasses"
    :to="action.to"
  >
  {{ action.label }}
  </router-link>
  `,
  computed: {
    decorationClasses: () => [
      'action-to-plan',
      'c-btn',
      `c-btn--${action.color}`,
      `c-btn--${action.color}--middle`,
    ],
    action: () => action,
  },
})

const noActionButton = (opt: { label: string, color: string }) => Vue.extend({
  name: 'NoActionButton',
  template: `
    <span class="no-action" style="color: ${ opt.color };">
      ${opt.label}
    </span>
  `,
})
const notSubscribed = noActionButton({ label: '変更できません', color: '#777777' })
const subscribed = noActionButton({ label: '', color: '#404040' })
const activeUntilEndOfMonth = noActionButton({ label: '解約済み<br />（月末まで有効）', color: '#404040' })
const reserved = noActionButton({ label: '来月から有効', color: '#404040' })
const holding = noActionButton({ label: '保留中', color: '#777777' })

@Component
export default class ActionToPlanTemporally extends Vue {
  @Prop({ required: true })
  readonly plan: AvailablePaidMembershipPlanInGroup

  get view(): VueConstructor {
    if (this.plan.holding()) {
      return holding
    }
    if (this.plan.upgrade.isAllowed()) {
      return actionLink(upgrade(this.plan.belongingTo, this.plan.upgrade))
    }
    if (this.plan.downgrade.isAllowed()) {
      return actionLink(downgrade(this.plan.belongingTo, this.plan.downgrade))
    }
    if (this.plan.canSubscirbeAsNew) {
      return actionLink(apply)
    }
    if (this.plan.reserved()) {
      return reserved
    }
    if (this.plan.subscribed()) {
      if (this.plan.canUnsubscribe) {
        return actionLink(unapply(this.plan))
      }

      return subscribed
    }
    if (this.plan.unsubscribed() && this.plan.isActive()) {
      return activeUntilEndOfMonth
    }

    return notSubscribed
  }
}
